import { inject, Injectable } from '@angular/core';
import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import { environment } from 'environments/environment';
import { Observable, Subject, delay, filter, from, lastValueFrom, map, merge, retry, shareReplay, switchMap, take, tap, } from 'rxjs';
import { SignalRBase } from 'app/classes/signalR-base';

@Injectable({
  providedIn: 'root'
})
export class SignalRService extends SignalRBase {
  private hubConnection: signalR.HubConnection = this.createHubConnection();
  private readonly onEvents$ = new Subject<{ methodName: string, args: any }>();
  private readonly onEvents = new Set<string>();
  private readonly onEventsV2$ = new Subject<{ methodName: string, args: any }>();
  private readonly onEventsV2 = new Set<string>();

  constructor() {
    super();
    this.connect().pipe().subscribe();
    this.userOrganizationChanged$.pipe(
      filter((organizationId) => organizationId !== this.organizationService.organizationSelectedId),
      tap(_ => this.snackBar.open('The selected organization has been changed...')),
      delay(2000)
    ).subscribe((organizationId) => this.organizationService.select(organizationId));
  }

  get userOrganizationChanged$() {
    return this.onV2('UserOrganizationChanged').pipe(
      map(args => {
        const { organizationId } = args;
        return organizationId
      })
    );
  }

  get organizationCreated$() {
    return this.on('OrganizationCreated').pipe(
      map(args => args[0] as string)
    );
  }

  get liveClientsStarted$() {
    return this.onV2('LiveClientStarted').pipe(
      map(args => {
        const { clientId, startedAt, sessionId, objectId, objectKind } = args;
        return { clientId, online: true, startedAt, totalSeconds: 0, sessionId, objectId, objectKind, closedAt: null };
      }),
      // shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  get liveClientsStopped$() {
    return this.onV2('LiveClientStopped').pipe(
      map(args => {
        const { clientId, closedAt, totalSeconds, sessionId } = args;
        return { clientId, closedAt, totalSeconds, online: false, sessionId, startedAt: null, objectId: null, objectKind: null };
      }),
      // shareReplay({ bufferSize: 1, refCount: true })
    );
  }


  private createHubConnection() {
    return new HubConnectionBuilder()//.configureLogging(LogLevel.Trace)
      .withUrl(`${environment.hubConnection}`, {
        accessTokenFactory: () => lastValueFrom(this.auth0Service.getAccessTokenSilently({ cacheMode: 'on' }).pipe(take(1), tap({
          error: (err) => {
            this.appMonitoringService.logException(err);
            console.error(err);
            this.auth0Service.logout();
          }
        })))
      }).withAutomaticReconnect([1000]).build();
  }

  public connect() {
    return merge(
      this.reconnect$.pipe(
        filter(() => this.hubConnection?.state === HubConnectionState.Disconnected || this.hubConnection?.state !== HubConnectionState.Connecting)
      ),
      this.auth0Service.isAuthenticated$.pipe(
        filter(isAuthenticated => !!isAuthenticated)
      )
    ).pipe(
      switchMap(() => {
        console.log('connect...');
        return from(this.hubConnection.start()).pipe(
          tap({
            next: () => {
              this.connected();
              console.log('SignalR connected');
              this.hubConnection.onreconnected(() => {
                console.log('onreconnected');
                this.connected();
              });
              this.hubConnection.onreconnecting((err) => {
                console.log('onreconnecting');
                this.reconnectingHandler(err);
              });
              this.hubConnection.onclose((err) => {
                console.log('SignalR close');
                this.hubConnection = this.createHubConnection();
                this.connectionErrorHandler(err);
              });
            },
            error: (err) => {
              console.log('Error hub connection');
              this.hubConnection = this.createHubConnection();
              this.connectionErrorHandler(err);
            }
          }),
          retry({ delay: () => this.reconnect$ }),
        )
      }),
    )
  }

  private connected() {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      this._isConnect$.next(true);
      this.resubscribedOn();
      this.hubConnection.invoke('SubscribeV2', { organizationId: this.organizationService.organizationSelectedId }).then(() => {
        console.log('SubscribeV2');
      }).catch((err) => console.error('Subscribe invoke error: ', err));
    }
  }

  on(methodName: string): Observable<Array<any>> {
    if (!this.onEvents.has(methodName)) {
      this.onEvents.add(methodName);
      this.hubConnection.on(methodName, (...args) => this.onEvents$.next({ methodName, args: [...args] }));
    }
    return this.isConnected$.pipe(
      filter(isConnect => isConnect),
      switchMap(() => this.onEvents$.asObservable()),
      filter((event) => event.methodName === methodName),
      map((event) => event.args),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  onV2(methodName: string): Observable<any> {
    if (!this.onEventsV2.has(methodName)) {
      this.onEventsV2.add(methodName);
      this.hubConnection.on(methodName, (args) => this.onEventsV2$.next({ methodName, args }));
    }
    return this.isConnected$.pipe(
      filter(isConnect => isConnect),
      switchMap(() => this.onEventsV2$.asObservable()),
      filter((event) => event.methodName === methodName),
      map((event) => event.args),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  invoke(methodName: string, ...args: any[]) {
    try {
      return this.hubConnection?.invoke(methodName, ...args).catch((err) => {
        console.error(err, `${methodName} args: `, ...args)
      });
    } catch (err) {
      console.error(err, `${methodName} args: `, ...args)
    }
  }

  invokeV2(methodName: string, args: any) {
    return this.hubConnection?.invoke(methodName, args).catch((err) => {
      console.error(err, `${methodName} args: `, args)
    });
  }


  private resubscribedOn() {
    Array.from(this.onEvents, (methodName) => {
      console.log('on: ', methodName);
      this.onEvents.add(methodName);
      this.hubConnection.on(methodName, (...args) => this.onEvents$.next({ methodName, args: [...args] }));
    });
    Array.from(this.onEventsV2, (methodName) => {
      console.log('onV2: ', methodName);
      this.onEventsV2.add(methodName);
      this.hubConnection.on(methodName, (args) => this.onEventsV2$.next({ methodName, args }));
    });
  }
}